import moment from 'moment';
import { scaleLinear } from 'd3-scale';
import { HabitatInsightsMetricType } from 'src/shared/types';
import { PortfolioSiteMetric, PredictedSpeciesList } from 'src/shared/types/graph/portfolio';
import { DataTableState, TableRow } from 'src/shared/components/data-table/dataTableTypes';

// Build a d3 color scale for a given metric key
const buildColorScale = (metric: string) => {
    if (metric === 'POSSIBLE_IUCN_RED_LIST_SPECIES') {
        return scaleLinear<string>().domain([0, 0.5, 1]).range(['#F4F4F2', '#F9DFE0', '#A71B1B']).clamp(true);
    }
    if (metric === 'POSSIBLE_INVASIVE_SPECIES') {
        return scaleLinear<string>().domain([0, 0.5, 1]).range(['#F4F4F2', '#CFE8FF', '#073A69']).clamp(true);
    }

    if (metric === HabitatInsightsMetricType.HUMAN_MODIFICATION_INDEX) {
        return scaleLinear<string>()
            .domain([0, 0.167, 0.333, 0.5, 0.667, 0.833, 1])
            .range(['#0F540A', '#4F752F', '#A4AB67', '#CCC682', '#CDA883', '#CB6685', '#CF1D8A'])
            .clamp(true);
    }

    if (metric === HabitatInsightsMetricType.BIODIVERSITY_INTACTNESS_INDEX) {
        return scaleLinear<string>()
            .domain([0, 0.167, 0.333, 0.5, 0.667, 0.833, 1])
            .range(['#CF1D8A', '#CB6685', '#CDA883', '#CCC682', '#A4AB67', '#4F752F', '#0F540A'])
            .clamp(true);
    }

    // Default scale used for other numeric metrics
    return scaleLinear<string>()
        .domain([0, 0.167, 0.333, 0.5, 0.667, 0.833, 1])
        .range(['#eaeaea', '#ccc682', '#91bf51', '#70a33f', '#4f892d', '#306d1c', '#0f540a', '#004400'])
        .clamp(true);
};

// Color helper – prefers metric-specific scale when provided
// Optional domain allows dynamic min/max normalization for species metrics
export const getPortfolioMetricColor = (value: number, metric?: string, domain?: { min: number; max: number }): string => {
    const scale = buildColorScale(metric || 'default');
    let normalized = value;
    if (domain && isFinite(domain.min) && isFinite(domain.max) && domain.max > domain.min) {
        normalized = (value - domain.min) / (domain.max - domain.min);
    }
    const safe = isFinite(normalized) ? Math.max(0, Math.min(1, normalized)) : 0;
    return scale(safe);
};

// Map a UI metric key to the backend metric name
const mapUiMetricToBackend = (metric: string): string => {
    if (metric === 'Vegetation_Condition') {
        return 'Primary_Productivity';
    }
    return metric;
};

// Extract a comparable numeric value for a site metric regardless of shape
export type GetPortfolioMetricValueParams = {
    siteMetrics?: PortfolioSiteMetric[];
    metric: string;
    redList?: number;
    invasive?: number;
};

export const getPortfolioMetricValue = ({ siteMetrics, metric, redList, invasive }: GetPortfolioMetricValueParams): number => {
    // Support sidebar extra metrics sourced from popup query
    if (metric === 'POSSIBLE_IUCN_RED_LIST_SPECIES') {
        // Remove decimal places
        return Number(redList?.toFixed(0) ?? 0);
    }
    if (metric === 'POSSIBLE_INVASIVE_SPECIES') {
        return Number(invasive?.toFixed(0) ?? 0);
    }

    if (!siteMetrics || siteMetrics.length === 0) {
        return 0;
    }

    // TODO: remove this once we have a unified metric names from the backend
    const backendMetricKey = mapUiMetricToBackend(metric);
    const metricData = siteMetrics.find(m => m?.name === backendMetricKey);
    if (!metricData) {
        return 0;
    }

    // Single numeric value for a year
    if (typeof metricData.data === 'number') {
        return metricData.data;
    }

    // Multi-year array of numbers: take latest entry
    if (Array.isArray(metricData.datum) && metricData.datum.length > 0) {
        const isVegtationHealth = metric === 'Primary_Productivity' || metric === 'Vegetation_Condition';
        const isConnectivityIndex = metric === 'All_Connectivity';
        // For Vegetation Health and Connectivity, take the last datum (most recent year)
        const datumIndex = isVegtationHealth || isConnectivityIndex ? metricData.datum.length - 1 : 0;
        return metricData.datum[datumIndex];
    }

    // Multi-metric map for a single year (e.g., Land Cover)
    type LandCoverYearData = Record<string, number>;
    type MetricWithTrend = Record<string, number | LandCoverYearData>;
    const metricWithTrend = metricData.metricWithTrend as MetricWithTrend | undefined;
    if (metricWithTrend) {
        const yearKeys = Object.keys(metricWithTrend);
        if (yearKeys.length === 0) return 0;
        const latestYear = yearKeys.sort((a, b) => Number(b) - Number(a))[0];
        const yearValue = metricWithTrend[latestYear];
        if (backendMetricKey === 'Land_Cover') {
            if (typeof yearValue === 'object') {
                const vegetation = (yearValue as LandCoverYearData)['Cumulative Vegetation Area'];
                return typeof vegetation === 'number' ? vegetation : 0;
            }
            return typeof yearValue === 'number' ? yearValue : 0;
        }
        return typeof yearValue === 'number' ? yearValue : 0;
    }

    // Multi-metric single year (object of categories): pick first numeric
    type MultiMetricSingleYear = { metrics?: Record<string, number> };
    const multiMetric = (metricData as MultiMetricSingleYear).metrics;
    if (multiMetric && typeof multiMetric === 'object') {
        const first = Object.values(multiMetric).find(v => typeof v === 'number');
        return typeof first === 'number' ? first : 0;
    }

    return 0;
};

// Get formatted table data for species risk indicators table
export type SpeciesPredictionDetails = {
    status: string;
    siteCoverageInfo: {
        percentCoverage: number;
        siteCount: number;
    };
    isNew?: boolean;
    isUpdated?: boolean;
};

type GetSpeciesRiskIndicatorsTableDataParams = {
    totalSites: number;
    isPredictionFirstRun: boolean;
    lastPredictionRun: string;
    speciesList: PredictedSpeciesList[];
    tab: 'invasive-species' | 'iucn-red-list-species';
};

export const getSpeciesRiskIndicatorsTableData = ({
    totalSites,
    isPredictionFirstRun,
    lastPredictionRun,
    speciesList,
    tab,
}: GetSpeciesRiskIndicatorsTableDataParams) => {
    const tableData: DataTableState['data'] = [];
    const isInvasivesTable = tab === 'invasive-species';

    // Extract just the date portion (YYYY-MM-DD) for comparison, ignoring time
    const lastPredictionRunDate = lastPredictionRun ? lastPredictionRun.split('T')[0] : '';

    speciesList.forEach(species => {
        const percentCoverage = Math.floor((species.siteCount / totalSites) * 100);

        //Evaluate dates to determine if the species is new or updated
        const predictionCreatedDate = species.predictionCreatedAt ? species.predictionCreatedAt.split('T')[0] : '';
        const predictionUpdatedDate = species.predictionLastUpdated ? species.predictionLastUpdated.split('T')[0] : '';

        const isCreatedAndUpdatedDateSame = !Boolean(predictionUpdatedDate) || predictionUpdatedDate === predictionCreatedDate;
        const isCreatedAndLastRunDateMatching = predictionCreatedDate >= lastPredictionRunDate;
        const isUpdatedAndLastRunDateMatching = Boolean(predictionUpdatedDate) && predictionUpdatedDate >= lastPredictionRunDate;
        const isCreatedIsBeforeUpdatedDate =
            Boolean(predictionUpdatedDate) && moment(predictionCreatedDate).isBefore(moment(predictionUpdatedDate));

        const speciesPredictionDetails: SpeciesPredictionDetails = {
            status: isInvasivesTable ? 'IN' : species.iucnRedListCategory,
            siteCoverageInfo: {
                percentCoverage: percentCoverage || 0,
                siteCount: species.siteCount || 0,
            },
            isNew: !isPredictionFirstRun && isCreatedAndUpdatedDateSame && isCreatedAndLastRunDateMatching,
            isUpdated: !isPredictionFirstRun && isCreatedIsBeforeUpdatedDate && isUpdatedAndLastRunDateMatching,
        };

        const formattedPredictionCreatedDate = moment(species.predictionCreatedAt).format('MMM YYYY');
        let rowData = [
            { data: speciesPredictionDetails },
            species.commonName,
            species.scientificName,
            formattedPredictionCreatedDate,
            species.iucnRedListCategory,
            species.siteCount,
        ];
        if (isInvasivesTable) {
            rowData = [
                { data: speciesPredictionDetails },
                species.commonName,
                species.scientificName,
                formattedPredictionCreatedDate,
                species.siteCount,
            ];
        }

        tableData.push(rowData);
    });

    return tableData;
};

export const modalTablesMergeComparator: (tableRows: TableRow[], columnIndex: number, columnValue: string) => boolean = (
    tableRows,
    columnIndex,
    columnValue
) => {
    // check if first row value same as the columnValue
    const firstRowValue = tableRows[0]?.cells[columnIndex];
    if (firstRowValue !== columnValue) {
        return false;
    }

    // Check and merge if all row values are same in the column
    return tableRows.every(row => row.cells[columnIndex] === firstRowValue);
};

/** Table cells hold the raw engine number so the grid can sort on it; this is how
 *  that number is shown. Areas arrive as unrounded floats (729.9699999999999),
 *  which would otherwise leak binary floating-point noise into the column. */
export const formatTableNumber = (value: number | null | undefined): string | null => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
        return null;
    }
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
};
